import Chart from "react-apexcharts";
import React from "react";

const DEFAULT_COLORS = ["#008FFB", "#00E396", "#FEB019", "#FF4560", "#775DD0", "#3F51B5"];

const DonutChart = ({ chartUtils, colors }) => {
  if (!chartUtils?.series || !Array.isArray(chartUtils.series)) {
    return <div>Loading chart...</div>;
  }

  // ── Resolve series values ──────────────────────────────────────────────
  const series = chartUtils.series.map((item) => {
    if (Array.isArray(item?.data) && item.data.length > 0) return Number(item.data[0]) || 0;
    return 0;
  });

  const isDataEmpty = series.every((val) => val === 0 || val === null);

  if (series.length === 0 || isDataEmpty) {
    return (
      <div style={{ textAlign: "center", padding: "20px", color: "#aaa" }}>
        No Report found
      </div>
    );
  }

  const total = series.reduce((sum, val) => sum + val, 0);
  const labels = chartUtils.series.map((item) => item?.name || "Unknown");

  // ── Resolved colors — always use a flat array, never gradient ─────────
  // Gradient fill is the cause of missing colors on single-slice donuts.
  // Using chart.foreColor + explicit colors[] array is the only reliable path.
  const resolvedColors = (colors?.length ? colors : DEFAULT_COLORS).slice(0, series.length);
  // Pad if fewer colors than series
  while (resolvedColors.length < series.length) {
    resolvedColors.push(DEFAULT_COLORS[resolvedColors.length % DEFAULT_COLORS.length]);
  }

  const options = {
    chart: {
      type: "donut",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: { enabled: true, delay: 150 },
        dynamicAnimation: { enabled: true, speed: 500 },
      },
    },
    // ── KEY FIX: colors at top level, not inside fill ──────────────────
    colors: resolvedColors,
    labels,
    // ── Disable gradient — it's what causes blank/white slices ────────
    fill: {
      type: "solid",
      opacity: 1,
    },
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => {
        const rawVal = series[opts.seriesIndex];
        return `${rawVal} (${val.toFixed(1)}%)`;
      },
      style: {
        fontSize: "11px",
        fontWeight: "bold",
        colors: ["#fff"],
      },
      dropShadow: { enabled: false },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: { show: false },
        },
        expandOnClick: false,
        dataLabels: {
          offset: 16,
          minAngleToShowLabel: 8,
        },
      },
    },
    stroke: {
      width: 2,
      colors: ["#fff"],
    },
    legend: { show: false },
    tooltip: {
      y: {
        formatter: (val) => `${val} (${((val / total) * 100).toFixed(1)}%)`,
      },
    },
    states: {
      hover:  { filter: { type: "darken", value: 0.85 } },
      active: { filter: { type: "darken", value: 0.7  } },
    },
  };

  // ── Custom legend ──────────────────────────────────────────────────────
  const CustomLegend = () => (
    <div style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: "0 20px",
      minWidth: 200,
    }}>
      {chartUtils.series.map((item, index) => {
        const value = series[index];
        const percentage = ((value / total) * 100).toFixed(1);
        const color = resolvedColors[index];
        return (
          <div key={index} style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 10,
            fontSize: 12,
          }}>
            <div style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: color,
              marginRight: 8,
              flexShrink: 0,
              // ── Inline style guarantees color even if CSS hasn't loaded ──
              boxShadow: `0 0 0 2px ${color}33`,
            }} />
            <span style={{ color: "#373d3f" }}>
              {item?.name || "Unknown"}: <strong>{value}</strong> ({percentage}%)
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{
      width: "100%",
      minHeight: 400,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <div style={{ flex: "0 0 auto" }}>
        <Chart
          options={options}
          series={series}
          type="donut"
          height={350}
          width={350}
        />
      </div>
      <CustomLegend />
    </div>
  );
};

export default DonutChart;